import { Card, CardContent, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { OnlyFansPayout } from '../../../../../../hooks/useMetricsDailyStats';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

// Helper function for currency formatting
const formatCurrency = (amount: number, withFraction: boolean = true): string => {
    return withFraction
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount)
        : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
};

// Get monthly revenue data for charts
const getMonthlyRevenueData = (
    latestEarnings: OnlyFansPayout.PayoutsStats | null | undefined,
): {
    month: number;
    net: number;
    gross: number;
    monthFormatted: string;
}[] => {
    if (!latestEarnings) return [];

    const monthlyData: { [key: string]: { month: number; net: number; gross: number } } = {};

    if (latestEarnings && latestEarnings.list && latestEarnings.list.months) {
        Object.keys(latestEarnings.list.months).forEach(month => {
            if (!latestEarnings.list.months[month]) return;

            const monthData = latestEarnings.list.months[month];

            if (!monthlyData[month]) {
                monthlyData[month] = {
                    month: parseInt(month, 10),
                    net: 0,
                    gross: 0,
                };
            }

            monthlyData[month].net += monthData.total_net || 0;
            monthlyData[month].gross += monthData.total_gross || 0;
        });
    }

    const result = Object.values(monthlyData).sort((a, b) => {
        return new Date(a.month).getTime() - new Date(b.month).getTime();
    });

    return result.map(item => ({
        ...item,
        monthFormatted: moment.unix(item.month).format('MMM YYYY'),
    }));
};

const RevenueChart: React.FC<DashboardProps> = ({
    subscriber,
    data,
    latestEarnings,
    isLatestEarningsLoading,
    isLoading,
    amountType,
    handleSetAmountType,
}) => {
    const theme = useTheme();

    if (isLatestEarningsLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ marginBottom: 2 }}>
                        <Skeleton variant="text" width={200} />
                        <Skeleton variant="rectangular" width={180} height={40} />
                    </Stack>
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                        <Typography variant="h6">Monthly Revenue</Typography>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const monthlyRevenueData = getMonthlyRevenueData(latestEarnings);

    if (monthlyRevenueData.length === 0) {
        return (
            <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                        <Typography variant="h6">Monthly Revenue</Typography>
                    </Stack>
                    <Typography variant="body1">No data available</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={3}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={2}>
                    <Typography variant="h6">Monthly Revenue</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ToggleButtonGroup
                            value={amountType}
                            exclusive
                            color="secondary"
                            size="small"
                            onChange={(_event, newValue) => {
                                if (!newValue) {
                                    return;
                                }

                                handleSetAmountType(newValue);
                            }}
                            sx={{ marginTop: '4px', height: '39px' }}
                        >
                            <ToggleButton value="gross" fullWidth sx={{ minWidth: '60px' }}>
                                GROSS
                            </ToggleButton>
                            <ToggleButton value="net" fullWidth sx={{ minWidth: '60px' }}>
                                NET
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <ExportButtons
                            data={monthlyRevenueData}
                            componentType="monthlyRevenue"
                            filename={`${subscriber.username}_Monthly_Revenue`}
                        />
                    </Stack>
                </Stack>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={monthlyRevenueData} margin={{ left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="month"
                            tick={{ fontSize: 11 }}
                            angle={-30}
                            textAnchor="end"
                            tickFormatter={value => moment.unix(value).format('MMMM YYYY')}
                        />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={value => formatCurrency(value, false)} />
                        <Tooltip
                            labelFormatter={label => `Month: ${moment.unix(label).format('MMMM YYYY')}`}
                            formatter={value => [formatCurrency(value as number), undefined]}
                        />
                        <Area
                            type="monotone"
                            dataKey={amountType === 'gross' ? 'gross' : 'net'}
                            name={amountType === 'gross' ? 'Gross Revenue' : 'Net Revenue'}
                            stroke={amountType === 'gross' ? theme.palette.primary.main : theme.palette.secondary.main}
                            fill={amountType === 'gross' ? theme.palette.primary.light : theme.palette.secondary.light}
                            fillOpacity={0.6}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default RevenueChart;
